import API_BASE_URL from '../config';
import { getAuthHeaders } from '../utils/authHeaders';

const COOLDOWN_UNTIL_KEY = 'function_app_version_check_cooldown_until';
const COOLDOWN_MS = 15 * 60 * 1000;

let loginListenerInstalled = false;

function ensureLoginListener(): void {
  if (loginListenerInstalled || typeof window === 'undefined') {
    return;
  }
  loginListenerInstalled = true;
  window.addEventListener('oidc-login-success', () => {
    sessionStorage.removeItem(COOLDOWN_UNTIL_KEY);
  });
}

export function clearFunctionVersionCheckCooldown(): void {
  sessionStorage.removeItem(COOLDOWN_UNTIL_KEY);
}

function isInAuthCooldown(): boolean {
  const raw = sessionStorage.getItem(COOLDOWN_UNTIL_KEY);
  if (!raw) {
    return false;
  }
  const until = Number(raw);
  if (Number.isNaN(until) || Date.now() >= until) {
    sessionStorage.removeItem(COOLDOWN_UNTIL_KEY);
    return false;
  }
  return true;
}

function enterAuthCooldown(): void {
  sessionStorage.setItem(COOLDOWN_UNTIL_KEY, String(Date.now() + COOLDOWN_MS));
}

let inflight: Promise<Record<string, unknown> | null> | null = null;

/**
 * Auth-protected version probe with 401/403 session cooldown, login reset, and in-flight dedupe.
 */
export async function fetchFunctionAppVersionPayload(): Promise<Record<string, unknown> | null> {
  ensureLoginListener();
  if (isInAuthCooldown()) {
    return null;
  }
  if (inflight) {
    return inflight;
  }
  const p = (async (): Promise<Record<string, unknown> | null> => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/getFunctionAppVersion`, {
        method: 'GET',
        headers,
        cache: 'no-store',
      });

      if (response.status === 401 || response.status === 403) {
        enterAuthCooldown();
        return null;
      }

      if (!response.ok) {
        return null;
      }

      sessionStorage.removeItem(COOLDOWN_UNTIL_KEY);
      return (await response.json()) as Record<string, unknown>;
    } catch {
      return null;
    }
  })();
  inflight = p;
  void p.finally(() => {
    if (inflight === p) {
      inflight = null;
    }
  });
  return p;
}
